import React, { useState, createContext } from "react";
import { UserContext } from "./User";
import { makeAxiosCall } from "utils";
import { BuildOrderStatusOptions, OrderItemInterface, OrderInterface, OrderStatusInterface, CreateEmptyOrder, fillOrderDefaults } from "interfaces/Order";
import { CompanyInterface } from "interfaces/Company";
import { AuthorizePaymentProfileInterface, CCDataInterface, CCInfoInterface } from "interfaces/Payment";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";


interface CartContextInterface {
    orderId?: number,
    setOrderId: (orderId: number | null) => void,
    ccInfo: CCInfoInterface,
    loadPreviousOrder: (order: OrderInterface) => void,
    deleteCartItem: (badProduct: OrderItemInterface) => void,
    updateCartItem: (product: OrderItemInterface) => void,
    addCartItem: (product: any) => void,
    clearCart: () => void,
    savePaymentOption: (paymentOption: CCDataInterface) => void,
    fetchCC: (gatewayProfileId: number) => void,
    orderingOnBehalfOf: CompanyInterface,
    setOrderAs: (company: CompanyInterface) => void,
    updateOrderAsCompany: (company: CompanyInterface) => void,
    orderStatusOptions: Array<OrderStatusInterface>,
    saveCurrentActiveStep: (step: number) => void,
    currentActiveStep: number,
    currentOrder: OrderInterface,
    saveCurrentOrder: (order: OrderInterface) => void,
    saveCurrentOrderNote: (note: string) => void,
    currentOrderNote: string,
    saveOrderingDemo: (demo: boolean) => void,
    // Demo and "order on behalf of" are mutually exclusive — switching either one
    // re-initializes the whole cart (addresses/contacts/pricing all change), after
    // a confirmation when the cart isn't empty.
    startDemo: () => void,
    cancelDemo: () => void,
    requestOrderAs: (company: CompanyInterface) => void,
}


export const CartContext = createContext<CartContextInterface>(null);

interface CartProviderInterface {
    children: any,
}

export const CartProvider = ({ children }: CartProviderInterface) => {
    const [paymentOption, setPaymentOption] = useState<AuthorizePaymentProfileInterface>();
    const [ccInfo, setCCInfo] = useState<CCInfoInterface>();
    const [orderingOnBehalfOf, setOrderingOnBehalfOf] = useState<CompanyInterface>();
    const [orderId, setOrderId] = useState<number | null>();
    const [currentActiveStep, setCurrentActiveStep] = useState<number | null>();
    const [orderStatusOptions, setOrderStatusOptions] = useState<OrderStatusInterface[]>([]);
    const [currentOrderNote, setCurrentOrderNote] = useState<string>("");

    const [currentOrder, setCurrentOrder] = useState<OrderInterface>();

    const { userToken, currentUser } = React.useContext(UserContext);

    const saveCurrentActiveStep = (step: number) => {
        setCurrentActiveStep(step);
        localStorage.setItem("checkout-step", "" + step);
    };

    const setOrderAs = (company: CompanyInterface) => {
        setOrderingOnBehalfOf(company);

        const updatedOrder = CreateEmptyOrder(company, currentUser);
        updatedOrder.orderItems = currentOrder.orderItems;
        // Only carry a saved order forward when staying on the SAME company.
        // Switching companies starts a fresh order for that company — keeping the
        // id would later UPDATE (and corrupt) the prior company's saved order, and
        // would pair a real id with the placeholder "New Order" status (id 0), which
        // the order API rejects ("missing order_status_id"). Cart items are kept.
        if (currentOrder.id && currentOrder.company?.id === company.id) {
            updatedOrder.id = currentOrder.id;
            updatedOrder.orderStatus = currentOrder.orderStatus;
        }
        saveCurrentOrder(updatedOrder);

        localStorage.setItem("orderas", JSON.stringify(company));
    };

    const saveCurrentOrderNote = (note: string) => {
        setCurrentOrderNote(note);
        localStorage.setItem("current-order-note", note);
    };

    const saveCurrentOrder = (order: OrderInterface) => {

        console.log("Saving order");
        console.log(order);


        setCurrentOrder(order);
        localStorage.setItem("current-order", JSON.stringify(order));
    };


    const saveOrderingDemo = (demo: boolean) => {
        saveCurrentOrder({ ...currentOrder, demoOrder: demo });
    };

    // --- Mutually-exclusive demo / order-on-behalf-of context switching ---
    // A demo is always the rep's OWN order (40% rep pricing, billed to the rep);
    // an "order on behalf of" is a reseller order and never a demo. Switching
    // either rebuilds the cart from scratch so addresses/contacts/pricing match the
    // new context and nothing stale carries over. Confirmed first if the cart has items.

    const [pendingSwitch, setPendingSwitch] = useState<{ company: CompanyInterface, isDemo: boolean, description: string } | null>(null);

    const switchOrderContext = (company: CompanyInterface, isDemo: boolean) => {
        if (!company || !currentUser) return;
        setOrderingOnBehalfOf(company);
        localStorage.setItem("orderas", JSON.stringify(company));
        const fresh = CreateEmptyOrder(company, currentUser);
        fresh.demoOrder = !!isDemo;
        saveCurrentOrder(fresh);
        saveCurrentActiveStep(0);
    };

    const requestOrderContextSwitch = (company: CompanyInterface, isDemo: boolean, description: string) => {
        if (currentOrder?.orderItems?.length > 0) {
            setPendingSwitch({ company, isDemo, description });
        } else {
            switchOrderContext(company, isDemo);
        }
    };

    const confirmContextSwitch = () => {
        if (pendingSwitch) switchOrderContext(pendingSwitch.company, pendingSwitch.isDemo);
        setPendingSwitch(null);
    };
    const cancelContextSwitch = () => setPendingSwitch(null);

    const startDemo = () => requestOrderContextSwitch(currentUser?.company, true, "to a demo order");
    const cancelDemo = () => requestOrderContextSwitch(currentUser?.company, false, "out of demo mode");
    const requestOrderAs = (company: CompanyInterface) => {
        const isOwn = company?.id === currentUser?.company?.id;
        requestOrderContextSwitch(
            company,
            false,
            isOwn ? "to ordering for your own company" : `to ordering on behalf of ${company?.name}`
        );
    };


    const savePaymentOption = (paymentOption: CCDataInterface) => {
        if (!paymentOption) {
            setPaymentOption(null);
            localStorage.removeItem("cart-payment-option");
        } else {
            if (paymentOption.address) {
                saveCurrentOrder({ ...currentOrder, billingAddress: paymentOption.address, authorizePaymentProfileId: paymentOption.card.authorizeCustomerPaymentProfileId });
            } else {
                saveCurrentOrder({ ...currentOrder, authorizePaymentProfileId: paymentOption.card.authorizeCustomerPaymentProfileId });
            }
            setPaymentOption({
                customerPaymentProfileId: paymentOption.card.authorizeCustomerPaymentProfileId,
                payment: {
                    creditCard: {
                        cardNumber: paymentOption.card.cardNumber,
                        expirationDate: paymentOption.card.expirationDate,
                        cardType: paymentOption.card.cardType,
                        issuerNumber: paymentOption.card.issuerNumber
                    }
                }
            });
            localStorage.setItem("cart-payment-option", JSON.stringify(paymentOption.card));
        }
    };

    // Refresh the order-as company's data (for pickers/validation) WITHOUT
    // rebuilding the order. Unlike setOrderAs, this preserves the PO file, PO
    // number, notes, payment selection, and in-progress address/contact picks.
    const updateOrderAsCompany = (company: CompanyInterface) => {
        setOrderingOnBehalfOf(company);
        localStorage.setItem("orderas", JSON.stringify(company));
    };

    const loadPreviousOrder = (order: OrderInterface) => {
        if (order.company.id != currentUser.company.id) {
            setOrderAs(order.company);
        }

        order.orderItems = order.orderItems.sort((a, b) => a.product.name.localeCompare(b.product.name));
        saveCurrentOrder(order);
    };

    const deleteCartItem = (badProduct: OrderItemInterface) => {
        let newCart = [...currentOrder.orderItems];

        newCart = newCart.filter((item: OrderItemInterface) =>
            !(item.product.id === badProduct.product.id && item.condition === badProduct.condition)
        );

        if (orderId) {
            makeAxiosCall(
                "delete",
                `order-item/${badProduct.id}`
            ).then(res => {
                console.log("Removed item from cart");
            }).catch(error => {
                console.error(error);
            });
        }

        newCart = newCart.sort((a, b) => a.product.name.localeCompare(b.product.name));
        saveCurrentOrder({ ...currentOrder, orderItems: newCart });
    };


    const updateCartItem = (product: OrderItemInterface) => {
        let newCart = currentOrder.orderItems.map((item: OrderItemInterface) =>
            item.product.id === product.product.id && item.condition === product.condition
                ? product
                : item
        );

        newCart = newCart.sort((a, b) => a.product.name.localeCompare(b.product.name));

        saveCurrentOrder({ ...currentOrder, orderItems: newCart });
    };

    const addCartItem = (product: OrderItemInterface) => {
        let newCart = [...currentOrder.orderItems];

        const existingIndex = newCart.findIndex(
            (item) => item.product.id === product.product.id && item.condition === product.condition
        );
        if (existingIndex >= 0) {
            newCart[existingIndex] = { ...newCart[existingIndex], quantity: newCart[existingIndex].quantity + product.quantity };
        } else {
            newCart.push(product);
        }

        newCart = newCart.sort((a, b) => a.product.name.localeCompare(b.product.name));

        saveCurrentOrder({ ...currentOrder, orderItems: newCart });
        saveCurrentActiveStep(0);
    };

    const clearCart = () => {
        localStorage.removeItem("checkout-step");
        localStorage.removeItem("orderas");

        if(!currentUser) {
            setOrderingOnBehalfOf(null);
        } else {
            setOrderingOnBehalfOf(currentUser.company);
            saveCurrentOrder(CreateEmptyOrder(currentUser.company, currentUser));
        }
    };

    const fetchCC = React.useCallback(
        async (gatewayProfileId) => {
            if (gatewayProfileId !== undefined) {
                makeAxiosCall(
                    "get",
                    "cc/" + gatewayProfileId,
                )
                    .then((res) => {
                        setCCInfo(res.data);
                    })
                    .catch((error) => console.log("Error:" + error));
            } else {
                setCCInfo(null);
            }
        },
        [userToken]
    );

    const loadOrderStatusOptions = () => {
        if (!orderStatusOptions || orderStatusOptions.length < 1) {
            makeAxiosCall(
                "get",
                "order-status-options",
            )
                .then((res) => {
                    const options = res.data;
                    if (options && options.length > 0) {
                        localStorage.setItem("order-status-options", JSON.stringify(res.data));
                        setOrderStatusOptions(BuildOrderStatusOptions(res.data));
                    }
                })
                .catch((error) => console.log("Error:" + error));
        }
    };


    React.useEffect(() => {
        if (currentUser && currentUser.company) {
            try {
                const localStatusOptions = localStorage.getItem("order-status-options");
                if (localStatusOptions) {
                    setOrderStatusOptions(JSON.parse(localStatusOptions));
                }

                const localActiveStep = localStorage.getItem("checkout-step");
                if (localActiveStep) {
                    setCurrentActiveStep(parseInt(localActiveStep));
                } else {
                    setCurrentActiveStep(0);
                }

                const localCurrentOrder = localStorage.getItem("current-order");
                if (localCurrentOrder) {
                    const parsed = JSON.parse(localCurrentOrder);
                    // Migrate old cart items to new condition-aware format
                    if (parsed.orderItems) {
                        parsed.orderItems = parsed.orderItems.map((item: any) => ({
                            ...item,
                            condition: item.condition || (item.fillWithBstock ? 'like_new' : 'new'),
                            product: {
                                ...item.product,
                                bstockLikeNew: item.product.bstockLikeNew ?? item.product.bstock ?? 0,
                                bstockRefurbished: item.product.bstockRefurbished ?? 0,
                            }
                        }));
                    }
                    // A cart can legitimately belong to another company when an admin/rep
                    // is "ordering as" that company — keep it intact so its id, PO, notes,
                    // addresses and shipping preference survive a refresh. Only rebuild
                    // (keeping items) when the cart belongs to neither the logged-in user
                    // nor the active "order as" target, i.e. it's genuinely stale from a
                    // previous account/company.
                    const orderAsRaw = localStorage.getItem("orderas");
                    const orderAsCompany = orderAsRaw ? JSON.parse(orderAsRaw) : null;
                    const isStaleCompany =
                        parsed.company?.id &&
                        parsed.company.id !== currentUser.company.id &&
                        parsed.company.id !== orderAsCompany?.id;
                    if (isStaleCompany) {
                        const freshOrder = CreateEmptyOrder(currentUser.company, currentUser);
                        freshOrder.orderItems = parsed.orderItems || [];
                        saveCurrentOrder(freshOrder);
                    } else {
                        setCurrentOrder(parsed);
                    }
                } else {
                    saveCurrentOrder(CreateEmptyOrder(currentUser.company, currentUser));
                }

                const localPaymentOption = localStorage.getItem("cart-payment-option");
                if (localPaymentOption) {
                    setPaymentOption(JSON.parse(localPaymentOption));
                }

                const localOA = localStorage.getItem("orderas");
                if (localOA) {
                    setOrderingOnBehalfOf(JSON.parse(localOA));
                } else if (!orderingOnBehalfOf) {
                    setOrderingOnBehalfOf(currentUser.company);
                }
            } catch (e) {
                console.log(e);
                clearCart();
            }

            let co: CompanyInterface;
            if (orderingOnBehalfOf) {
                co = orderingOnBehalfOf;
            } else {
                co = currentUser.company;
            }

            if (co && currentOrder) {
                const updated = fillOrderDefaults(currentOrder, co);
                if (updated !== currentOrder) saveCurrentOrder(updated);
            }

            if (orderStatusOptions.length < 1) {
                loadOrderStatusOptions();
            }
        }
    }, [currentUser]);

    return (
        <CartContext.Provider
            value={{
                orderId,
                setOrderId,
                ccInfo,
                loadPreviousOrder,
                deleteCartItem,
                updateCartItem,
                addCartItem,
                savePaymentOption,
                fetchCC,
                orderingOnBehalfOf,
                setOrderAs,
                updateOrderAsCompany,
                clearCart,
                orderStatusOptions,
                saveCurrentActiveStep,
                currentActiveStep,
                currentOrder,
                saveCurrentOrder,
                saveCurrentOrderNote,
                currentOrderNote,
                saveOrderingDemo,
                startDemo,
                cancelDemo,
                requestOrderAs
            }}
        >
            {children}
            <Dialog open={!!pendingSwitch} onClose={cancelContextSwitch}>
                <DialogTitle>Reset your current order?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Switching {pendingSwitch?.description} will reset your cart. Continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelContextSwitch}>Cancel</Button>
                    <Button onClick={confirmContextSwitch} variant="contained" color="primary" autoFocus>
                        Reset &amp; Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </CartContext.Provider>
    );
};
